<template>
  <div class="ps-overlay" v-if="visible" @click.self="$emit('close')">
    <div class="ps-dialog">
      <div class="ps-header">
        <span>选择{{ tabLabels[activeTab] }}</span>
        <button class="ps-close" @click="$emit('close')">&times;</button>
      </div>
      <!-- Tab 切换（仅多模式时显示） -->
      <div class="ps-tabs" v-if="allowedModes.length > 1">
        <button v-for="key in allowedModes" :key="key" :class="['ps-tab', { active: activeTab === key }]" @click="switchTab(key)">{{ tabLabels[key] }}</button>
      </div>
      <!-- 搜索 -->
      <div class="ps-search">
        <input v-model="keyword" @input="doSearch" placeholder="输入关键词搜索..." class="ps-search-input" />
      </div>
      <!-- 列表/树 -->
      <div class="ps-list" ref="listRef">
        <div v-if="loading" class="ps-loading">加载中...</div>
        <div v-else-if="flatItems.length === 0" class="ps-empty">无匹配结果</div>
        <!-- 用户列表（平铺） -->
        <template v-if="activeTab === 'user'">
          <div v-for="item in flatItems" :key="getItemKey(item)" class="ps-item" @click="select(item)">
            <span class="ps-item-toggle"></span>
            <span class="ps-item-name">{{ getItemName(item) }}</span>
            <span class="ps-item-code">{{ getItemCode(item) }}</span>
          </div>
        </template>
        <!-- 组织/角色（树） -->
        <template v-else>
          <div v-for="item in flatItems" :key="getItemKey(item)" class="ps-item" @click="select(item)" :style="{ paddingLeft: (14 + (item.__depth || 0) * 18) + 'px' }">
            <span class="ps-item-toggle" @click.stop="toggleExpand(item)">
              <span v-if="hasChildren(item)" class="ps-toggle-icon" :class="{ expanded: isExpanded(item) }">▶</span>
            </span>
            <span class="ps-item-name">{{ getItemName(item) }}</span>
            <span class="ps-item-code">{{ getItemCode(item) }}</span>
          </div>
        </template>
      </div>
      <div class="ps-footer">
        <span class="ps-hint">已选: <b>{{ selectedSummary || '—' }}</b></span>
        <button class="ps-btn-cancel" @click="$emit('close')">取消</button>
        <button class="ps-btn-ok" @click="confirmSelect">确定</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { getUserList } from '@/api/user'
import { getOrgTree, getOrgList } from '@/api/org'
import { getRoleTree, getRoleList } from '@/api/role'

const props = defineProps({
  visible: { type: Boolean, default: false },
  /** 'user' | 'org' | 'role' */
  sourceType: { type: String, default: 'user' },
  /** 控制显示的 Tab，默认全显示。如 ['user'] 只显示用户 */
  modes: { type: Array, default: () => ['user', 'org', 'role'] }
})
const emit = defineEmits(['close', 'selected'])

const tabLabels = { user: '用户', org: '组织', role: '角色' }
const allowedModes = computed(() => props.modes.filter(m => tabLabels[m]))
const activeTab = ref(allowedModes.value.includes(props.sourceType) ? props.sourceType : (allowedModes.value[0] || 'user'))
const keyword = ref('')
const items = ref([])          // 原始数据：用户为数组，组织/角色为树
const loading = ref(false)
const expandedKeys = ref(new Set())  // 已展开节点（按 guid/code）
let searchTimer = null

// 暂存当前选中（仅供"确定"按钮使用，单选模式仍可点击行直接选中）
const pendingSelection = ref(null)

const selectedSummary = computed(() => pendingSelection.value ? pendingSelection.value.name : '')

/**
 * 把树拍平：默认仅显示顶层节点，节点展开后插入其 children。
 * 字段提取统一在此处完成（兼容 camelCase 与 PascalCase）。
 */
const flatItems = computed(() => {
  if (activeTab.value === 'user') {
    return items.value || []
  }
  const result = []
  const walk = (nodes, depth) => {
    ;(nodes || []).forEach(n => {
      const key = getItemKey(n)
      result.push({ ...n, __depth: depth })
      if (expandedKeys.value.has(key) && hasChildren(n)) walk(getChildren(n), depth + 1)
    })
  }
  walk(items.value, 0)
  return result
})

function switchTab(key) {
  activeTab.value = key
  keyword.value = ''
  pendingSelection.value = null
  expandedKeys.value = new Set()
  doSearch()
}

/** 通用字段提取：兼容 camelCase 与 PascalCase */
function pickField(obj, camelName, pascalName, fallback) {
  if (!obj) return fallback ?? ''
  if (obj[camelName] !== undefined && obj[camelName] !== null && obj[camelName] !== '') return obj[camelName]
  if (obj[pascalName] !== undefined && obj[pascalName] !== null && obj[pascalName] !== '') return obj[pascalName]
  return fallback ?? ''
}

function getItemName(item) {
  if (activeTab.value === 'user') {
    return pickField(item, 'displayName', 'DisplayName',
      pickField(item, 'realName', 'RealName',
        pickField(item, 'name', 'Name', '')))
  }
  if (activeTab.value === 'org') {
    return pickField(item, 'ousName', 'OUsName', pickField(item, 'name', 'Name', ''))
  }
  // role
  return pickField(item, 'roleName', 'RoleName', pickField(item, 'name', 'Name', ''))
}

function getItemCode(item) {
  if (activeTab.value === 'user') {
    return pickField(item, 'account', 'Account', pickField(item, 'code', 'Code', ''))
  }
  if (activeTab.value === 'org') {
    return pickField(item, 'ousCode', 'OUsCode', pickField(item, 'code', 'Code', ''))
  }
  return pickField(item, 'roleCode', 'RoleCode', pickField(item, 'code', 'Code', ''))
}

function getItemKey(item) {
  // 优先用 guid，否则用 code/name 拼一个稳定 key
  const g = pickField(item, 'guid', 'Guid', '')
  if (g) return g
  return `${getItemCode(item)}__${getItemName(item)}`
}

function hasChildren(item) {
  const ch = getChildren(item)
  return Array.isArray(ch) && ch.length > 0
}

function getChildren(item) {
  if (activeTab.value === 'user') return null
  return item?.children || item?.Children || null
}

function isExpanded(item) { return expandedKeys.value.has(getItemKey(item)) }

function toggleExpand(item) {
  if (!hasChildren(item)) return
  const key = getItemKey(item)
  if (expandedKeys.value.has(key)) expandedKeys.value.delete(key)
  else expandedKeys.value.add(key)
  // 触发响应式
  expandedKeys.value = new Set(expandedKeys.value)
}

function doSearch() {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(async () => {
    loading.value = true
    try {
      if (activeTab.value === 'user') {
        const res = await getUserList(keyword.value ? { keyword: keyword.value } : {})
        items.value = unwrap(res)
        // 后端搜索精确度有限时，前端再过滤一次
        if (keyword.value) {
          const kw = keyword.value.toLowerCase()
          items.value = items.value.filter(u => {
            const name = (getItemName(u) || '').toLowerCase()
            const code = (getItemCode(u) || '').toLowerCase()
            return name.includes(kw) || code.includes(kw)
          })
        }
      } else if (activeTab.value === 'org') {
        // 优先用 tree 接口（自带父子层级），fallback 平铺
        let res
        try {
          res = await getOrgTree(keyword.value || '')
        } catch (e) {
          console.warn('[PersonSelector] org/tree 调用失败，回退 list:', e)
          res = await getOrgList()
        }
        items.value = unwrap(res)
        if (!Array.isArray(items.value)) items.value = []
        // 不是树结构（fallback 场景）→ 把所有节点视作根
        const hasTree = items.value.some(n => hasChildren(n))
        if (!hasTree && items.value.length) {
          items.value = items.value.map(n => ({ ...n, children: [] }))
        }
      } else if (activeTab.value === 'role') {
        let res
        try {
          res = await getRoleTree(keyword.value || '')
        } catch (e) {
          console.warn('[PersonSelector] role/tree 调用失败，回退 list:', e)
          res = await getRoleList()
        }
        items.value = unwrap(res)
        if (!Array.isArray(items.value)) items.value = []
        const hasTree = items.value.some(n => hasChildren(n))
        if (!hasTree && items.value.length) {
          items.value = items.value.map(n => ({ ...n, children: [] }))
        }
      }
      // 搜索关键词时默认展开所有匹配节点（便于查看）
      if (keyword.value && activeTab.value !== 'user') {
        expandAll(items.value)
      }
    } catch (e) {
      console.error('PersonSelector search error:', e)
      items.value = []
    } finally {
      loading.value = false
    }
  }, 300)
}

function expandAll(nodes) {
  if (!Array.isArray(nodes)) return
  nodes.forEach(n => {
    const key = getItemKey(n)
    if (key) expandedKeys.value.add(key)
    if (hasChildren(n)) expandAll(getChildren(n))
  })
  expandedKeys.value = new Set(expandedKeys.value)
}

/** 把 axios 响应解开为数据数组 */
function unwrap(res) {
  // 兼容多种格式：{ data: { Data: [...] } } / { data: [...] } / [...]
  const candidates = []
  candidates.push(res?.data?.data)
  candidates.push(res?.data?.Data)
  candidates.push(res?.Data)
  candidates.push(res?.data)
  candidates.push(res)
  for (const c of candidates) {
    if (Array.isArray(c)) return c
    if (c && typeof c === 'object' && c.guid !== undefined) return [c]  // 单个对象
  }
  return []
}

function select(item) {
  pendingSelection.value = {
    type: activeTab.value,
    code: getItemCode(item),
    name: getItemName(item)
  }
  // 单选：立刻返回并关闭
  emit('selected', pendingSelection.value)
}

function confirmSelect() {
  if (pendingSelection.value) {
    emit('selected', pendingSelection.value)
  }
}

watch(() => props.visible, (v) => {
  if (v) {
    activeTab.value = allowedModes.value.includes(props.sourceType) ? props.sourceType : (allowedModes.value[0] || 'user')
    keyword.value = ''
    pendingSelection.value = null
    expandedKeys.value = new Set()
    doSearch()
  }
})
watch(() => props.sourceType, (t) => { if (t && allowedModes.value.includes(t)) activeTab.value = t })
</script>

<style scoped>
.ps-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.35); z-index: 9999; display: flex; align-items: center; justify-content: center; }
.ps-dialog { width: 440px; max-height: 560px; background: #fff; border-radius: 10px; box-shadow: 0 8px 32px rgba(0,0,0,.18); display: flex; flex-direction: column; overflow: hidden; }
.ps-header { padding: 12px 16px; font-size: 14px; font-weight: 600; color: #1f2937; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; }
.ps-close { background: none; border: none; font-size: 20px; color: #9ca3af; cursor: pointer; padding: 0 4px; line-height: 1; }
.ps-close:hover { color: #374151; }
.ps-tabs { display: flex; border-bottom: 1px solid #e5e7eb; padding: 0 12px; gap: 2px; }
.ps-tab { flex: 1; padding: 8px 0; border: none; background: none; font-size: 12px; color: #6b7280; cursor: pointer; border-bottom: 2px solid transparent; transition: all .15s; }
.ps-tab.active { color: #10b981; border-bottom-color: #10b981; font-weight: 600; }
.ps-tab:hover { color: #374151; }
.ps-search { padding: 8px 12px; }
.ps-search-input { width: 100%; border: 1px solid #d1d5db; border-radius: 6px; padding: 6px 10px; font-size: 12px; outline: none; box-sizing: border-box; }
.ps-search-input:focus { border-color: #10b981; box-shadow: 0 0 0 2px rgba(16,185,129,.1); }
.ps-list { flex: 1; overflow-y: auto; max-height: 340px; }
.ps-loading, .ps-empty { padding: 24px; text-align: center; font-size: 12px; color: #9ca3af; }
.ps-item { display: flex; align-items: center; gap: 10px; padding: 8px 14px; cursor: pointer; border-bottom: 1px solid #f3f4f6; transition: background .15s; }
.ps-item:hover { background: #ecfdf5; }
.ps-item-toggle { display: inline-flex; width: 16px; justify-content: center; }
.ps-toggle-icon { display: inline-block; font-size: 9px; color: #9ca3af; transition: transform .15s; user-select: none; }
.ps-toggle-icon.expanded { transform: rotate(90deg); }
.ps-item-name { font-size: 14px; color: #1f2937; font-weight: 500; flex: 1; }
.ps-item-code { font-size: 12px; color: #6b7280; background: #f3f4f6; padding: 2px 8px; border-radius: 4px; font-family: 'Consolas', 'Courier New', monospace; }
.ps-footer { padding: 8px 12px; border-top: 1px solid #e5e7eb; display: flex; justify-content: flex-end; align-items: center; gap: 8px; }
.ps-hint { flex: 1; font-size: 12px; color: #6b7280; }
.ps-hint b { color: #10b981; }
.ps-btn-cancel { padding: 5px 16px; border: 1px solid #d1d5db; border-radius: 6px; background: #fff; font-size: 12px; color: #6b7280; cursor: pointer; }
.ps-btn-cancel:hover { background: #f3f4f6; }
.ps-btn-ok { padding: 5px 16px; border: none; border-radius: 6px; background: #10b981; color: #fff; font-size: 12px; cursor: pointer; }
.ps-btn-ok:hover { background: #059669; }
</style>